import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    LayoutDashboard,
    BookOpen,
    Users,
    ShoppingCart,
    MessageSquare,
    Settings,
    LogOut,
    Menu,
    X,
    ChevronRight,
    Bell,
    FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuth } from "@/hooks/useAuth";

const AdminLayout = ({ children }: { children: React.ReactNode }) => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout: authLogout } = useAuth();
    const adminEmail = user?.email || 'Admin';

    // Responsive effect: close sidebar on navigation (mobile) or window resize
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 1024) {
                setIsSidebarOpen(true);
            } else {
                setIsSidebarOpen(false);
            }
        };

        handleResize(); // Set initial state
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (window.innerWidth < 1024) {
            setIsSidebarOpen(false);
        }
    }, [location.pathname]);

    const menuItems = [
        { label: 'Dashboard', icon: LayoutDashboard, href: '/admin/dashboard' },
        { label: 'Courses', icon: BookOpen, href: '/admin/courses' },
        { label: 'Blogs', icon: FileText, href: '/admin/blogs' },
        { label: 'Users', icon: Users, href: '/admin/users' },
        { label: 'Payments / Orders', icon: ShoppingCart, href: '/admin/orders' },
        { label: 'Enquiries', icon: MessageSquare, href: '/admin/leads' },
        { label: 'Settings', icon: Settings, href: '/admin/settings' },
    ];

    const handleLogout = async () => {
        await authLogout();
        navigate('/admin/login');
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC] flex text-slate-900 font-sans max-w-full overflow-x-hidden">
            {/* Overlay for mobile */}
            {isSidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden animate-fade-in"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside className={cn(
                "fixed inset-y-0 left-0 z-50 bg-[#001B3D] text-white transition-all duration-300 flex flex-col shadow-2xl lg:shadow-none",
                isSidebarOpen ? "translate-x-0 w-64" : "-translate-x-full lg:translate-x-0 w-20"
            )}>
                {/* Brand */}
                <div className="h-20 flex items-center px-6 border-b border-white/10 shrink-0">
                    <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center shrink-0">
                        <span className="font-black text-primary text-xl">R</span>
                    </div>
                    {isSidebarOpen && (
                        <div className="ml-4 overflow-hidden">
                            <p className="font-black uppercase tracking-tighter text-sm whitespace-nowrap">Admin Panel</p>
                            <p className="text-[10px] text-accent/60 font-black uppercase tracking-widest whitespace-nowrap">Management</p>
                        </div>
                    )}
                </div>

                {/* Navigation */}
                <nav className="flex-1 overflow-y-auto py-6 px-4 space-y-2 custom-scrollbar">
                    {menuItems.map((item) => {
                        const isActive = location.pathname === item.href;
                        return (
                            <Link
                                key={item.href}
                                to={item.href}
                                className={cn(
                                    "flex items-center h-12 rounded-xl transition-all duration-300 group relative",
                                    isActive ? "bg-accent/10 text-accent" : "hover:bg-white/5 text-slate-400 hover:text-white",
                                    !isSidebarOpen && "justify-center"
                                )}
                            >
                                <item.icon className={cn("w-5 h-5 shrink-0", isActive ? "text-accent" : "text-inherit")} />
                                {isSidebarOpen && (
                                    <>
                                        <span className="ml-4 text-xs font-black uppercase tracking-widest">{item.label}</span>
                                        {isActive && <div className="absolute right-0 w-1.5 h-6 bg-accent rounded-l-full" />}
                                    </>
                                )}
                                {!isSidebarOpen && isActive && <div className="absolute right-0 w-1 h-8 bg-accent rounded-l-full" />}
                            </Link>
                        );
                    })}
                </nav>

                {/* Bottom Actions */}
                <div className="p-4 border-t border-white/10 space-y-2">
                    <button
                        onClick={handleLogout}
                        className={cn(
                            "w-full flex items-center h-12 rounded-xl text-slate-400 hover:text-destructive hover:bg-destructive/10 transition-all duration-300 group",
                            !isSidebarOpen && "justify-center"
                        )}
                    >
                        <LogOut className="w-5 h-5 shrink-0" />
                        {isSidebarOpen && <span className="ml-4 text-xs font-black uppercase tracking-widest">Logout</span>}
                    </button>
                </div>
            </aside>

            {/* Main Content Pane */}
            <main className={cn(
                "flex-1 flex flex-col transition-all duration-300 min-h-screen max-w-full overflow-x-hidden",
                isSidebarOpen ? "lg:pl-64" : "lg:pl-20"
            )}>
                {/* Header */}
                <header className="h-20 bg-white border-b border-slate-200 sticky top-0 z-40 px-4 md:px-8 flex items-center justify-between shadow-sm">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                            {isSidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </button>
                        <h1 className="text-sm font-black uppercase tracking-widest text-slate-400">
                            Admin <span className="text-slate-900 mx-2">/</span> {menuItems.find(m => m.href === location.pathname)?.label || 'Overview'}
                        </h1>
                    </div>

                    <div className="flex items-center gap-4 md:gap-6">
                        <div className="flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2 bg-slate-50 border border-slate-100 rounded-full max-w-[160px] md:max-w-none">
                            <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-primary/5 flex items-center justify-center border border-primary/10 shrink-0">
                                <Users className="w-3.5 h-3.5 md:w-4 md:h-4 text-primary" />
                            </div>
                            <div className="flex flex-col overflow-hidden">
                                <span className="text-[8px] md:text-[9px] font-black uppercase tracking-widest text-slate-400 leading-none mb-1">User</span>
                                <span className="text-[10px] font-black text-slate-900 leading-none truncate max-w-[80px] md:max-w-none">{adminEmail}</span>
                            </div>
                        </div>
                        <button className="relative p-2 hover:bg-slate-100 rounded-full transition-colors">
                            <Bell className="w-5 h-5 text-slate-400" />
                            <div className="absolute top-2 right-2 w-2 h-2 bg-accent rounded-full border-2 border-white" />
                        </button>
                    </div>
                </header>

                {/* Content Area */}
                <div className="p-4 md:p-8 flex-1">
                    {children}
                </div>
            </main>
        </div>
    );
};

export default AdminLayout;
